import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@mui/styles';
import { useNavigate } from 'react-router-dom';

import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

import PolicyIcon from '@mui/icons-material/Policy';

import { get as _get } from 'lodash';
import { format as D3format } from 'd3';

import Validator, { useValidate, checkValidity } from '../../../../components/validator';
import PopupMenu from '../../../../components/popupmenu';
import DashboardService from '../../../../data/dashboard-service';
import PolicyService from '../../../../data/policy-service';
import { GetISODateFromQuick } from '../../../../libraries/helpers';
import DateRange from './../components/date-range';
import { VerifyDates } from '../../widget';
import { GetContrastText } from '../../../../d3/utils';
import Auth, { ParamsSerializer } from '../../../../data/auth-service';
import Sparkline from '../../../../components/charts/sparkline';

const useStyles = makeStyles(theme => ({
    // editor classes
    editorRoot: {
        padding: theme.spacing(1),
    },
    editorFooter: {
        alignSelf: 'flex-end',
        width: '100%',
    },
    editorMargin: {
        margin: theme.spacing(2),
    },
    filterText: {
        marginLeft: 10,
    },
    primary: {
        backgroundColor: theme.palette.primary.main,
    },
    inlineForm: {
        margin: theme.spacing(1),
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
    label: {
        lineHeight: 2.5,
        opacity: 0.8,
    },
    count: {
        display: 'block',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    inlineChips: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
}));

const DEFAULTS = edit => edit
    ? { minW: 12, minH: 12 }
    : { minW: 3, minH: 10 };

// don't display until size-me can figure out what our size is
const AlertsPoliciesWidget = (props) => {
    return !props.size || !props.size.width ? ''
        : (
            props.data.edit
                ? <Editor { ...props }/>
                : <Widget { ...props } />
        );
};
AlertsPoliciesWidget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
};

const DataService = {
    count: d => d.count,
    timestamp: d => new Date(d.timestamp),
};

const Widget = (props) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [timeline, setTimeline] = useState();
    const [, setErrors] = useState(0);
    const [start, setStart] = useState(props.data.start);
    const [end, setEnd] = useState();

    const alertPerms = Auth.permissions.get('Alerts');
    const suricyPerms = Auth.permissions.get('SuricataRulesPolicy');

    const fetch = controller => {
        props.setBusy(true);
        const start = props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start;
        const end = props.data.quick ? undefined : props.data.end;
        setStart(start);
        setEnd(end);
        if (alertPerms.read && suricyPerms.read) {
            return DashboardService.getPolicyAlerts({
                start,
                policy: props.data.settings.policy,
                timeline,
            }, controller).catch(err => {
                props.onError(err, props.data.title);
                setErrors(current => {
                    props.setBusy(false, current > 2);
                    if (current > 2) DashboardService.unsubscribe(props.data.id);
                    return current + 1;
                });
                return 'error';
            });
        }
    };

    const callback = res => {
        if (res === 'error') return;
        setErrors(0);
        props.setBusy(false);
        (res || []).forEach(r => {
            if (_get(r, 'policy_stats.timeline')) {
                r.policy_stats.timeline = r.policy_stats.timeline
                    .sort((a, b) => new Date(a.timestamp).getTime() -
                        new Date(b.timestamp).getTime());
            }
        });
        setData((res || [])
            .sort((a, b) => {
                if (b.total_alert_count + a.total_alert_count > 0) {
                    return b.total_alert_count - a.total_alert_count;
                }
                return a.name.localeCompare(b.name);
            }));
    };

    const subscribe = () => {
        setErrors(0);
        if (alertPerms.read && suricyPerms.read) {
            DashboardService.subscribe(props.data.id, fetch, callback);
        }
    };

    const handlePolicyClick = n => {
        navigate(`/policies/suricata/rules/${n}`);
    };

    const handleAlertsClick = policy => {
        const filter = {
            operator: 'And',
            nodes: [
                { variable: 'event_source', value: 'suricata' },
                {
                    operator: 'Or',
                    nodes: [
                        ..._get(policy, 'sensors', []).map(s => (
                            { variable: 'sensor_uuid', value: s }
                        ))
                    ]
                },
            ]
        };
        const p = {
            start,
            end,
            quick: props.data.quick,
            filter: JSON.stringify(filter),
        };
        navigate(`/views/alerts?${ParamsSerializer(p)}`);
    };

    const isMC2 = p => p.endsWith('.mc2');

    // policy color doesn't look good enough to justify the query
    // const getPolicyColor = s => {
    //     // don't use _get because of potential '.' in policy names
    //     return ((policies || {})[s] || { color: theme.palette.info.light }).color;
    // };

    // const fetchPolicies = async () => {
    //     PolicyService.getPolicies('suricata_rules').then(res => {
    //         const policies = (res || []).reduce((a, c) => ({ ...a, [c.name]: c }), {});
    //         setPolicies(policies);
    //         console.log(policies);
    //     });
    // };

    useEffect(() => {
        setErrors(0);
        // fetchPolicies();
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, []);

    useEffect(() => {
        setTimeline(props.size.width >= 250 && props.data.settings.timeline);
    }, [props.data.settings.timeline, props.size.width]);

    useEffect(() => {
        subscribe();
        setStart(props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start);
        setEnd(props.data.quick ? undefined : props.data.end);
    }, [timeline, props.data.start, props.data.end, props.data.quick]);

    return (
        <div
            className={classes.root}
            style={{ width: props.size.width, height: props.size.height, overflow: 'auto' }}>

            {data.map((d, i) => (
                <List key={`${d.name}_${i}`}>
                    <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                            <Avatar className={classes.primary}>
                                <PolicyIcon/>
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                isMC2(d.name) ? d.name
                                    : <Link
                                        aria-label={d.name}
                                        onClick={() => handlePolicyClick(d.name)}
                                        style={{ cursor: 'pointer' }}>
                                        {d.name}
                                    </Link>
                            }
                            secondary={
                                <React.Fragment>
                                    <Grid
                                        container
                                        spacing={2}
                                        component="span"
                                        direction="row">
                                        <Grid item component="span">
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                className={classes.label}
                                                color="textPrimary">
                                                {d.mc2
                                                    ? <b style={{ paddingRight: 10 }}>MC2 Managed</b>
                                                    : <span>Rules:
                                                        <b style={{ paddingRight: 10 }}>
                                                            {D3format(',')(d.enabled_rule_count)}
                                                        </b>
                                                    </span>
                                                }
                                            </Typography>
                                        </Grid>
                                        <Grid item component="span">
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                className={classes.label}
                                                color="textPrimary">
                                                Sensors:  <b>
                                                    {D3format(',')((d.sensors || []).length)}
                                                </b>
                                                <br />
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid
                                        container
                                        spacing={3}
                                        component="span"
                                        direction="row"
                                        alignItems="flex-end">
                                        <Grid item component="span">
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                color="textPrimary"
                                                className={classes.count}>
                                                <span style={{ fontSize: 'small', opacity: 0.7 }}>
                                                    Alerts per policy: &nbsp;
                                                </span>
                                                <Link
                                                    aria-label={d.name}
                                                    onClick={() => handleAlertsClick(d)}
                                                    style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                                    {D3format(',')(d.total_alert_count)}
                                                </Link>
                                            </Typography>
                                        </Grid>
                                        {timeline && d.policy_stats && d.total_alert_count > 0 &&
                                            <Grid item component="span">
                                                <Sparkline
                                                    dataservice={DataService}
                                                    data={{
                                                        count: d.total_alert_count,
                                                        start,
                                                        end,
                                                        interval: d.policy_stats.interval,
                                                        objects: d.policy_stats.timeline || [],
                                                    }}
                                                    uuid={d.name}
                                                    width={100}
                                                    height={20}/>
                                            </Grid>}
                                    </Grid>
                                </React.Fragment>
                            }
                        />
                    </ListItem>
                    {i < data.length - 1 && <Divider variant="inset" component="li" />}
                </List>
            ))}
        </div>
    );
};
Widget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    hover: PropTypes.bool,
    setBusy: PropTypes.func,
    onError: PropTypes.func,
};

const Editor = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const daForm = useValidate({});

    const [title, setTitle] = useState(props.data.title);
    const [policies, setPolicies] = useState([]);
    const [policy, setPolicy] = useState(props.data.settings.policy || []);
    const [timeline, setTimeline] = useState(_get(props.data, 'settings.timeline', true));
    const [range, setRange] = useState({
        start: props.data.start,
        end: props.data.end,
        quick: props.data.quick,
    });

    const suriPerms = Auth.permissions.get('SuricataRulesPolicy');

    const handleRangeChange = range => {
        setRange({ ...range });
    };

    const handleTitleChange = (e, validID, valid) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        setTitle(e.target.value);
    };

    const handleRemovePolicy = s => {
        const i = policy.findIndex(src => s === src);
        if (i >= 0) policy.splice(i, 1);
        setPolicy([...policy]);
    };

    const handleAddPolicy = s => {
        const i = policy.findIndex(src => s.name === src.name);
        if (i < 0) policy.push(s.name);
        setPolicy([...policy]);
    };

    const getPolicyStyle = s => {
        // don't use _get because of potential '.' in policy names
        const color = ((policies || {})[s] || { color: theme.palette.background.paper }).color;
        return {
            color: GetContrastText(color, theme),
            backgroundColor: color,
        };
    };

    const handleOnTimeline = e => {
        setTimeline(e.currentTarget.checked);
    };

    const handleSaveChanges = () => {
        props.onOK({
            ...props.data,
            ...range,
            title,
            settings: {
                ...props.data.settings,
                timeline,
                policy,
            },
        });
    };

    const isValid = () => {
        if (!VerifyDates({ ...range, override: props.data.date_override })) return false;
        
        const formState = daForm.get() || {};
        if (Object.keys(formState).length === 0 && title && title.trim().length > 0) {
            return true;
        }
        return checkValidity(formState);
    };

    const fetchPolicies = async () => {
        if (suriPerms.read) {
            PolicyService.getPolicies('suricata_rules').then(res => {
                setPolicies((res || []).reduce((a, c) => ({ ...a, [c.name]: c }), {}));
            });
        }
    };

    useEffect(() => {
        console.log('policies', Object.values(policies || {}));
    }, [policies]);

    // initial hook
    useEffect(() => {
        fetchPolicies();
    }, []);

    return (
        <Box
            display="flex" flexWrap="wrap"
            className={classes.editorRoot}
            style={{ width: props.size.width - 5, height: props.size.height - 20 }}>
            <Box>
                <div className={classes.inlineForm}>
                    <Validator
                        style={{ width: 200 }}
                        label="Title"
                        aria-label="Title"
                        value={title}
                        onChange={handleTitleChange}/>
                    {props.data.date_override &&
                        <DateRange
                            onChange={handleRangeChange}
                            schedule={range} />
                    }
                </div>

                <div className={classes.editorMargin}>
                    <PopupMenu
                        disabled={(policy || []).length >= 3}
                        items={Object.values(policies || {})}
                        itemkey="name"
                        itemTitle="name"
                        onSelect={handleAddPolicy}
                        button={<Button
                            aria-label="add policy"
                            title="add policy"
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<AddIcon/>}>Policy
                        </Button>}
                        placement={'bottom-start'}
                        wrap={'true'}/>
                    {(policy || []).map((s, i) =>
                        <div key={s} className={classes.inlineChips}>
                            <Chip
                                style={getPolicyStyle(s)}
                                color={'default'}
                                label={s}
                                onDelete={() => handleRemovePolicy(s)}
                                className={classes.chip}/>
                        </div>
                    )}
                </div>

                <div>
                    <FormControlLabel
                        labelPlacement="start"
                        label="Show Timeline"
                        control={
                            <Checkbox
                                checked={!!timeline}
                                name=""
                                onChange={handleOnTimeline}/>
                        }/>
                </div>
            </Box>
            <Box className={classes.editorFooter}>
                <DialogActions style={{ paddingBottom: 0 }}>
                    <Button
                        aria-label="ok"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={!isValid()}>
                    OK
                    </Button>
                    <Button aria-label="cancel" onClick={props.onCancel} color="secondary" autoFocus>
                    Cancel
                    </Button>
                </DialogActions>
            </Box>
        </Box>
    );
};
Editor.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    onOK: PropTypes.func,
    onCancel: PropTypes.func,
};

export {
    AlertsPoliciesWidget as default,
    DEFAULTS,
};
